import { useEffect, useMemo } from "react";
import useAuthStore from "../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../Zustand/Store/usePermissionDataStore";
import { resolveOrganizationUserId } from "../lib/organizationSession";

const LEGACY_PERMISSION_STORAGE_KEY = "hr-permission-store";

/**
 * Loads permissions once at app level (not per-route).
 * Re-checks on tab focus so inactivated users are logged out (same idea as EMS).
 */
export default function PermissionBootstrap() {
  const { userData, isLoggedIn } = useAuthStore();
  const {
    fetchPermissions,
    refreshPermissions,
    resetPermissions,
    hasPermissionsForUser,
  } = usePermissionDataStore();

  const normalizedOrgUserId = useMemo(() => {
    const id = resolveOrganizationUserId(userData);
    if (id == null || id === "") return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  }, [userData]);

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_PERMISSION_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !isLoggedIn) {
      resetPermissions();
      return;
    }

    if (!normalizedOrgUserId) return;

    // Load once for this org user; do not force-refetch if already loaded.
    const alreadyLoaded = hasPermissionsForUser(normalizedOrgUserId);
    fetchPermissions(normalizedOrgUserId, { force: !alreadyLoaded }).catch(
      (error) => {
        console.error("Permission bootstrap error:", error);
      }
    );
  }, [
    normalizedOrgUserId,
    isLoggedIn,
    fetchPermissions,
    resetPermissions,
    hasPermissionsForUser,
  ]);

  useEffect(() => {
    const onPermissionsChanged = () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      const orgUserId = resolveOrganizationUserId(
        useAuthStore.getState().userData
      );
      if (!orgUserId) return;

      // Silent: update store without blanking the UI
      refreshPermissions(orgUserId, { silent: true }).catch((error) => {
        console.error("Permission refresh error:", error);
      });
    };

    window.addEventListener("hrerp:permissions-changed", onPermissionsChanged);

    return () => {
      window.removeEventListener(
        "hrerp:permissions-changed",
        onPermissionsChanged
      );
    };
  }, [refreshPermissions]);

  // When tab becomes visible again, re-check access so an inactivated user is logged out.
  useEffect(() => {
    const recheckSession = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      const token = localStorage.getItem("token");
      if (!token || !isLoggedIn) return;

      const orgUserId = resolveOrganizationUserId(
        useAuthStore.getState().userData
      );
      if (!orgUserId) return;

      refreshPermissions(orgUserId, { silent: true }).catch((error) => {
        console.error("Session access recheck error:", error);
      });
    };

    window.addEventListener("focus", recheckSession);
    document.addEventListener("visibilitychange", recheckSession);

    return () => {
      window.removeEventListener("focus", recheckSession);
      document.removeEventListener("visibilitychange", recheckSession);
    };
  }, [isLoggedIn, refreshPermissions]);

  return null;
}
